const euro = (value) => `${Math.round(value).toLocaleString('it-IT')} €`;
const score = (value) => Number(value).toLocaleString('it-IT', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const palette = {
  primary: '#1d4ed8',
  primarySoft: '#93c5fd',
  accent: '#f97316',
  accentSoft: '#fdba74',
  green: '#16a34a',
  red: '#dc2626',
  blue: '#0ea5e9',
  slate: '#334155',
  grid: '#e2e8f0',
  text: '#475569',
};

const valueLabelPlugin = {
  id: 'valueLabelPlugin',
  afterDatasetsDraw(chart, _args, pluginOptions) {
    if (!pluginOptions?.enabled) return;
    const { ctx } = chart;
    const isHorizontal = chart.options?.indexAxis === 'y';
    ctx.save();
    ctx.font = '600 11px Inter, Arial, sans-serif';
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = isHorizontal ? 'left' : 'center';
    ctx.textBaseline = 'middle';

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (dataset.skipDataLabels) return;
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden || meta.type === 'radar') return;

      meta.data.forEach((element, index) => {
        const rawValue = dataset.data[index];
        if (rawValue === null || rawValue === undefined || typeof rawValue === 'object') return;

        let label = `${rawValue}`;
        if (dataset.dataLabelType === 'euro') label = euro(rawValue);
        if (dataset.dataLabelType === 'percent') label = `${String(rawValue).replace('.', ',')}%`;
        if (dataset.dataLabelType === 'score') label = score(rawValue);
        if (dataset.dataLabelType === 'gap') label = `${rawValue.toLocaleString('it-IT')} €`;

        const { x, y } = element.tooltipPosition();
        if (meta.type === 'bar') {
          if (isHorizontal) ctx.fillText(label, x + 8, y);
          else ctx.fillText(label, x, y - 10);
        } else if (meta.type === 'line') {
          ctx.fillText(label, x, y - 12);
        }
      });
    });

    ctx.restore();
  },
};

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    valueLabelPlugin: { enabled: true },
    legend: {
      labels: {
        font: { family: 'Inter, Arial, sans-serif', size: 12 },
        color: palette.slate,
      },
    },
    tooltip: {
      callbacks: {
        label(context) {
          return `${context.dataset.label || ''}: ${context.raw}`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: { color: palette.text, font: { family: 'Inter, Arial, sans-serif' } },
      grid: { color: palette.grid },
    },
    y: {
      ticks: { color: palette.text, font: { family: 'Inter, Arial, sans-serif' } },
      grid: { color: palette.grid },
    },
  },
};

function createChart(id, config) {
  const canvas = document.getElementById(id);
  if (!canvas || !window.Chart) return null;
  return new Chart(canvas, config);
}

function initDistribuzioneRal() {
  return createChart('report-chart-distribuzione-ral', {
    type: 'bar',
    data: {
      labels: ['Sotto 25k', '25k-35k', '35k-45k', '45k-60k', 'Oltre 60k'],
      datasets: [{
        label: '% del campione',
        data: [7.4, 31.7, 26.7, 20.5, 13.6],
        dataLabelType: 'percent',
        backgroundColor: ['#dbeafe', palette.primary, palette.primary, '#bfdbfe', '#dbeafe'],
        borderRadius: 8,
      }],
    },
    options: {
      ...baseOptions,
      indexAxis: 'y',
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          callbacks: {
            label(context) {
              return `${context.raw}%`;
            },
          },
        },
      },
      scales: {
        x: {
          ...baseOptions.scales.x,
          beginAtZero: true,
          max: 35,
          ticks: {
            ...baseOptions.scales.x.ticks,
            callback: (value) => `${value}%`,
          },
        },
        y: baseOptions.scales.y,
      },
    },
  });
}

function initRalSeniority() {
  return createChart('report-chart-ral-seniority', {
    type: 'bar',
    data: {
      labels: ['Entry-Level', 'Junior', 'Mid-Level', 'Senior', 'Lead', 'Manager', 'Director'],
      datasets: [{
        label: 'RAL mediana',
        data: [28000, 30000, 36000, 45000, 50621, 63000, 90000],
        dataLabelType: 'euro',
        backgroundColor: ['#bfdbfe', '#93c5fd', '#60a5fa', palette.accent, '#2563eb', '#1d4ed8', '#1e3a8a'],
        borderRadius: 8,
      }],
    },
    options: {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          callbacks: {
            label(context) {
              return euro(context.raw);
            },
          },
        },
      },
      scales: {
        ...baseOptions.scales,
        y: {
          ...baseOptions.scales.y,
          beginAtZero: true,
          ticks: {
            ...baseOptions.scales.y.ticks,
            callback: (value) => `${Number(value / 1000)}k`,
          },
        },
      },
    },
  });
}

function initModalita() {
  return createChart('report-chart-modalita', {
    type: 'bar',
    data: {
      labels: ['Full remote', 'Ibrido', 'Presenza'],
      datasets: [
        {
          label: 'RAL mediana (€)',
          data: [42000, 40000, 37000],
          dataLabelType: 'euro',
          backgroundColor: palette.primary,
          borderRadius: 6,
          yAxisID: 'y1',
        },
        {
          label: 'Soddisfazione /10',
          data: [6.7, 6.1, 5.6],
          dataLabelType: 'score',
          backgroundColor: palette.green,
          borderRadius: 6,
          yAxisID: 'y',
        },
        {
          label: 'Stress /10',
          data: [6.0, 6.3, 6.7],
          dataLabelType: 'score',
          backgroundColor: palette.red,
          borderRadius: 6,
          yAxisID: 'y',
        },
        {
          label: 'Crescita /10',
          data: [6.3, 5.7, 5.2],
          dataLabelType: 'score',
          backgroundColor: palette.blue,
          borderRadius: 6,
          yAxisID: 'y',
        },
      ],
    },
    options: {
      ...baseOptions,
      scales: {
        ...baseOptions.scales,
        y: {
          ...baseOptions.scales.y,
          beginAtZero: true,
          max: 10,
          position: 'left',
        },
        y1: {
          position: 'right',
          beginAtZero: true,
          max: 50000,
          grid: { drawOnChartArea: false },
          ticks: {
            color: palette.text,
            callback: (value) => `${Number(value / 1000)}k`,
            font: { family: 'Inter, Arial, sans-serif' },
          },
        },
      },
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          callbacks: {
            label(context) {
              if (context.dataset.yAxisID === 'y1') return `${context.dataset.label}: ${euro(context.raw)}`;
              return `${context.dataset.label}: ${score(context.raw)}`;
            },
          },
        },
      },
    },
  });
}

const radarSource = [
  { label: 'Data Science', values: [6.2, 4.0, 6.2], color: '#2563eb' },
  { label: 'Customer Support', values: [6.2, 4.2, 5.3], color: '#0ea5e9' },
  { label: 'IT', values: [6.0, 3.9, 5.6], color: '#16a34a' },
  { label: 'Ingegneria', values: [6.0, 3.7, 5.6], color: '#84cc16' },
  { label: 'Ricerca', values: [6.2, 3.7, 5.2], color: '#f97316' },
  { label: 'Finanza', values: [5.8, 3.5, 5.8], color: '#f59e0b' },
  { label: 'Operations', values: [6.1, 3.2, 5.7], color: '#14b8a6' },
  { label: 'Marketing', values: [5.7, 3.2, 5.4], color: '#ec4899' },
  { label: 'Prodotto', values: [6.0, 2.8, 5.9], color: '#8b5cf6' },
  { label: 'Vendite', values: [5.9, 3.0, 5.4], color: '#ef4444' },
  { label: 'HR', values: [5.2, 3.1, 5.3], color: '#64748b' },
  { label: 'Legale', values: [5.0, 4.1, 4.8], color: '#0f766e' },
];

function buildRadarDatasets(selectedLabels) {
  return radarSource
    .filter((item) => selectedLabels.includes(item.label))
    .map((item) => ({
      label: item.label,
      data: item.values,
      borderColor: item.color,
      backgroundColor: `${item.color}22`,
      pointBackgroundColor: item.color,
      pointRadius: 3,
      fill: true,
    }));
}

function initBenessereAmbiti() {
  const controls = document.getElementById('report-chart-radar-controls');
  const defaultSelection = ['Data Science', 'Customer Support', 'IT', 'Ingegneria'];
  if (!controls) return null;

  const checkboxes = radarSource.map((item) => {
    const label = document.createElement('label');

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.value = item.label;
    input.checked = defaultSelection.includes(item.label);

    const dot = document.createElement('span');
    dot.style.width = '0.75rem';
    dot.style.height = '0.75rem';
    dot.style.display = 'inline-block';
    dot.style.borderRadius = '999px';
    dot.style.backgroundColor = item.color;

    label.append(input, dot, document.createTextNode(item.label));
    controls.appendChild(label);
    return input;
  });

  const chart = createChart('report-chart-benessere-ambiti', {
    type: 'radar',
    data: {
      labels: ['Soddisfazione', 'Stress sostenibile', 'Crescita'],
      datasets: buildRadarDatasets(defaultSelection),
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1.35,
      plugins: {
        legend: {
          labels: {
            font: { family: 'Inter, Arial, sans-serif', size: 12 },
            color: palette.slate,
          },
        },
        tooltip: {
          callbacks: {
            label(context) {
              return `${context.dataset.label}: ${score(context.raw)}`;
            },
          },
        },
      },
      scales: {
        r: {
          min: 0,
          max: 7,
          ticks: {
            backdropColor: 'transparent',
            color: palette.text,
            stepSize: 1,
          },
          grid: { color: palette.grid },
          angleLines: { color: palette.grid },
          pointLabels: {
            color: palette.slate,
            font: { family: 'Inter, Arial, sans-serif', size: 12 },
          },
        },
      },
    },
  });

  const refreshDisabledState = () => {
    const activeCount = checkboxes.filter((input) => input.checked).length;
    checkboxes.forEach((input) => {
      input.disabled = activeCount >= 4 && !input.checked;
    });
  };

  const sync = (changedInput) => {
    const active = checkboxes.filter((input) => input.checked);
    if (active.length === 0 && changedInput) {
      changedInput.checked = true;
      refreshDisabledState();
      return;
    }
    if (active.length > 4 && changedInput) {
      changedInput.checked = false;
      refreshDisabledState();
      return;
    }
    chart.data.datasets = buildRadarDatasets(active.map((input) => input.value));
    chart.update();
    refreshDisabledState();
  };

  checkboxes.forEach((input) => {
    input.addEventListener('change', () => sync(input));
  });

  refreshDisabledState();
  return chart;
}

function initBenessereSeniority() {
  return createChart('report-chart-benessere-seniority', {
    type: 'line',
    data: {
      labels: ['Entry-Level', 'Junior', 'Mid-Level', 'Senior', 'Lead', 'Manager', 'Director'],
      datasets: [
        {
          label: 'Soddisfazione',
          data: [5.9, 5.8, 5.8, 5.9, 6.4, 6.4, 7.0],
          dataLabelType: 'score',
          borderColor: palette.green,
          backgroundColor: palette.green,
          tension: 0.3,
          pointRadius: 4,
        },
        {
          label: 'Stress',
          data: [5.6, 5.9, 6.4, 6.7, 7.0, 7.0, 7.5],
          dataLabelType: 'score',
          borderColor: palette.red,
          backgroundColor: palette.red,
          tension: 0.3,
          pointRadius: 4,
        },
        {
          label: 'Crescita',
          data: [5.8, 5.6, 5.4, 5.3, 5.9, 6.1, 6.7],
          dataLabelType: 'score',
          borderColor: palette.blue,
          backgroundColor: palette.blue,
          tension: 0.3,
          pointRadius: 4,
        },
      ],
    },
    options: {
      ...baseOptions,
      scales: {
        ...baseOptions.scales,
        y: {
          ...baseOptions.scales.y,
          min: 4,
          max: 8,
        },
      },
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          callbacks: {
            label(context) {
              return `${context.dataset.label}: ${score(context.raw)}`;
            },
          },
        },
      },
    },
  });
}

function initGapGenere() {
  return createChart('report-chart-gap-genere', {
    type: 'bar',
    data: {
      labels: ['Entry-Level', 'Junior', 'Mid-Level', 'Senior', 'Lead', 'Manager', 'Director'],
      datasets: [{
        label: 'Gap donne vs uomini',
        data: [-5702, 0, -5000, -5000, -4000, -7375, -2000],
        dataLabelType: 'gap',
        backgroundColor: ['#fdba74', '#cbd5e1', '#fdba74', '#fdba74', '#fdba74', palette.accent, '#fdba74'],
        borderRadius: 8,
      }],
    },
    options: {
      ...baseOptions,
      indexAxis: 'y',
      scales: {
        x: {
          ...baseOptions.scales.x,
          min: -9000,
          max: 1000,
          ticks: {
            ...baseOptions.scales.x.ticks,
            callback: (value) => `${Number(value).toLocaleString('it-IT')} €`,
          },
        },
        y: baseOptions.scales.y,
      },
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          callbacks: {
            label(context) {
              return euro(context.raw);
            },
          },
        },
      },
    },
  });
}

function initTitoloStudio() {
  return createChart('report-chart-titolo-studio', {
    type: 'bubble',
    data: {
      datasets: [
        { label: 'Master', data: [{ x: 25000, y: 45000, r: 15 }], backgroundColor: '#2563eb99' },
        { label: 'Diploma', data: [{ x: 20000, y: 36800, r: 16 }], backgroundColor: '#16a34a99' },
        { label: 'PhD', data: [{ x: 29000, y: 49800, r: 14 }], backgroundColor: '#f9731699' },
        { label: 'Laurea magistrale', data: [{ x: 25000, y: 41000, r: 13 }], backgroundColor: '#0ea5e999' },
        { label: 'Laurea triennale', data: [{ x: 24000, y: 36000, r: 10 }], backgroundColor: '#64748b99' },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            font: { family: 'Inter, Arial, sans-serif', size: 12 },
            color: palette.slate,
          },
        },
        tooltip: {
          callbacks: {
            label(context) {
              const point = context.raw;
              return `${context.dataset.label}: primo ${euro(point.x)}, RAL ${euro(point.y)}, crescita ${Math.round((point.r / 2) * 100) / 100}%`;
            },
          },
        },
      },
      scales: {
        x: {
          min: 18000,
          max: 31000,
          ticks: {
            color: palette.text,
            callback: (value) => `${Number(value / 1000)}k`,
            font: { family: 'Inter, Arial, sans-serif' },
          },
          grid: { color: palette.grid },
          title: {
            display: true,
            text: 'Primo stipendio (€)',
            color: palette.slate,
          },
        },
        y: {
          min: 34000,
          max: 52000,
          ticks: {
            color: palette.text,
            callback: (value) => `${Number(value / 1000)}k`,
            font: { family: 'Inter, Arial, sans-serif' },
          },
          grid: { color: palette.grid },
          title: {
            display: true,
            text: 'RAL attuale (€)',
            color: palette.slate,
          },
        },
      },
    },
    plugins: [{
      id: 'bubbleLabels',
      afterDatasetsDraw(chart) {
        const { ctx } = chart;
        ctx.save();
        ctx.fillStyle = '#0f172a';
        ctx.font = '600 11px Inter, Arial, sans-serif';
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.data.forEach((point) => {
            const { x, y } = point.tooltipPosition();
            ctx.fillText(dataset.label, x, y - 16);
          });
        });
        ctx.restore();
      },
    }],
  });
}

function init(retries = 20) {
  if (!window.Chart) {
    if (retries > 0) setTimeout(() => init(retries - 1), 100);
    return;
  }

  Chart.register(valueLabelPlugin);
  initDistribuzioneRal();
  initRalSeniority();
  initModalita();
  initBenessereAmbiti();
  initBenessereSeniority();
  initGapGenere();
  initTitoloStudio();
}

window.addEventListener('DOMContentLoaded', init);
